import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  MagnifyingGlass,
  Plus,
  X,
} from "@phosphor-icons/react";
import { ChangeEvent, useState } from "react";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { StyledTextField } from "../../../../components/Common/StyledTextField";
import { IFluxBotModel } from "../../../../data/models/flux-bot";
import { useDarkMode } from "../../../../hooks/useDarkMode";
import { FlowService } from "../../../../service/FlowService";
import { FolderService } from "../../../../service/FolderService";
import { UserService } from "../../../../service/UserService";
import { WidgetType } from "../../../Dashboard/Widgets/types";
import { widgetTypes } from "../../../Dashboard/Widgets/widgetTypes";
import { useDashboard } from "../../Context/DashboardContext";
import { FlowTreeView } from "./FlowTreeView";
import { Folder } from "../../../../util/Types/Folder";

interface WidgetSelectionModalProps {
  open: boolean;
  onClose: () => void;
}

export function WidgetSelectionModal({
  open,
  onClose,
}: WidgetSelectionModalProps) {
  const { addWidget } = useDashboard();
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();

  const [isClosing, setIsClosing] = useState(false);
  const [currentStep, setCurrentStep] = useState<
    "main" | "chatbots" | "form" | "flows"
  >("main");
  const [chatbots, setChatbots] = useState<IFluxBotModel[]>([]);
  const [filteredChatbots, setFilteredChatbots] = useState<IFluxBotModel[]>([]);
  const [flows, setFlows] = useState<any[]>([]);
  const [filteredFlows, setFilteredFlows] = useState<any[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [spaces, setSpaces] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingChatbots, setIsLoadingChatbots] = useState(false);
  const [isLoadingFlows, setIsLoadingFlows] = useState(false);
  const [formUrl, setFormUrl] = useState("");

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setCurrentStep("main");
      setSearchTerm("");
    }, 300);
  };

  const handleBackToMain = () => {
    setCurrentStep("main");
    setSearchTerm("");
    setFormUrl("");
  };

  const handleChatbotSelection = () => {
    setCurrentStep("chatbots");
    loadChatbots();
  };

  const handleFormSelection = () => {
    setCurrentStep("form");
  };

  const handleFlowSelection = () => {
    setCurrentStep("flows");
    loadFlows();
  };

  const loadChatbots = async () => {
    setIsLoadingChatbots(true);
    try {
      const response = await FlowService.getFluxBots();
      setChatbots(response.data);
      setFilteredChatbots(response.data);
    } catch (error) {
      console.error("Error loading chatbots:", error);
    } finally {
      setIsLoadingChatbots(false);
    }
  };

  const loadFlows = async () => {
    setIsLoadingFlows(true);
    try {
      const [foldersResponse, spacesResponse] = await Promise.all([
        FolderService.getFolders(),
        UserService.getSpaces(),
      ]);
      setFolders(foldersResponse.data || []);
      setSpaces(spacesResponse.data || []);

      const flowsResponse = await FlowService.getFlows();
      setFlows(flowsResponse.data || []);
      setFilteredFlows(flowsResponse.data || []);
    } catch (error) {
      console.error("Error loading flows:", error);
    } finally {
      setIsLoadingFlows(false);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (currentStep === "chatbots") {
      if (value.trim() === "") {
        setFilteredChatbots(chatbots);
      } else {
        const filtered = chatbots.filter(
          (chatbot) =>
            chatbot.name.toLowerCase().includes(value.toLowerCase()) ||
            chatbot.space.toLowerCase().includes(value.toLowerCase()),
        );
        setFilteredChatbots(filtered);
      }
    } else if (currentStep === "flows") {
      if (value.trim() === "") {
        setFilteredFlows(flows);
      } else {
        const filtered = flows.filter((flow) =>
          flow.name?.toLowerCase().includes(value.toLowerCase()),
        );
        setFilteredFlows(filtered);
      }
    }
  };

  async function handleAddChatbotWidget(chatbot: IFluxBotModel) {
    await addWidget({ type: "chatbot", widgetData: chatbot });
    handleClose();
  }

  async function handleAddFormWidget(url: string) {
    let formUrl = url.trim();

    const formIdMatch = formUrl.match(/\/(?:form|published-form)\/([^\/\?]+)/);
    const formId = formIdMatch ? formIdMatch[1] : formUrl;

    const formData = {
      url: formUrl.startsWith("http")
        ? formUrl
        : `${window.location.origin}${
            formUrl.startsWith("/") ? formUrl : "/" + formUrl
          }`,
      id: formId,
      name: "Form",
    };

    await addWidget({ type: "form", widgetData: formData });
    handleClose();
  }

  async function handleAddFlowWidget(flow: any) {
    const isForm = (flow as any).isForm === true;
    let flowData = {
      id: flow.id,
      name: flow.name || (isForm ? "Interface" : "Flow"),
      nodes: flow.nodes || [],
      edges: flow.edges || [],
      space: flow.spaceId || flow.space || "private",
      isForm: isForm,
    };

    if (!flowData.nodes.length && !flowData.edges.length) {
      try {
        const response = await FlowService.getFlow(flow.id);
        flowData.nodes = response.data.nodes || [];
        flowData.edges = response.data.edges || [];
        flowData.isForm = response.data.isForm || isForm;
      } catch (error) {
        console.error("Error fetching flow data:", error);
      }
    }

    await addWidget({
      type: isForm ? "form" : "flow",
      widgetData: flowData,
    });
    handleClose();
  }

  return (
    <Modal
      open={open && !isClosing}
      onClose={handleClose}
      aria-labelledby="widget-selection-modal"
      aria-describedby="select-widget-type"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
      }}
    >
      <Fade in={open && !isClosing} timeout={300}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 810.43,
            height: 755,
            bgcolor: isDarkMode ? palette.gray[900] : "background.paper",
            borderRadius: "16px",
            boxShadow: 24,
            p: 4,
            outline: "none",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform:
              open && !isClosing
                ? "translate(-50%, -50%) scale(1)"
                : "translate(-50%, -50%) scale(0.95)",
          }}
        >
          {currentStep === "main" ? (
            <>
              <Fade in={open && !isClosing} timeout={350}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      color: isDarkMode ? palette.gray[100] : palette.gray[900],
                    }}
                  >
                    All Widgets
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <X size={24} />
                  </IconButton>
                </Box>
              </Fade>

              <Grid container spacing={2}>
                {widgetTypes.map((widgetType: WidgetType, index: number) => (
                  <Grid item xs={12} key={widgetType.id}>
                    <Fade in={open && !isClosing} timeout={400 + index * 100}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          if (widgetType.id === "chatbot") {
                            handleChatbotSelection();
                          } else if (widgetType.id === "form") {
                            handleFormSelection();
                          } else if (widgetType.id === "agent") {
                            handleFlowSelection();
                          } else {
                            handleClose();
                          }
                        }}
                        sx={{
                          p: 2,
                          border: `1px solid ${
                            isDarkMode ? palette.gray[700] : "#e0e0e0"
                          }`,
                          borderRadius: "28px",
                          textTransform: "none",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 3,
                          justifyContent: "space-between",
                          color: isDarkMode ? palette.gray[200] : "#333",
                          "&:hover": {
                            borderColor: isDarkMode
                              ? palette.gray[600]
                              : "#ccc",
                            backgroundColor: isDarkMode
                              ? "rgba(255,255,255,0.04)"
                              : "rgba(0,0,0,0.02)",
                            boxShadow: isDarkMode
                              ? "none"
                              : "10px 10px 40px 0px #0000001A",
                          },
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 3 }}
                        >
                          <Box
                            sx={{
                              backgroundColor: isDarkMode
                                ? palette.gray[800]
                                : "#f5f5f5",
                              borderRadius: "8px",
                              width: "48px",
                              height: "48px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isDarkMode ? palette.gray[400] : "#666",
                            }}
                          >
                            {widgetType.icon}
                          </Box>
                          <Box sx={{ textAlign: "left" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: isDarkMode
                                  ? palette.gray[100]
                                  : "inherit",
                              }}
                            >
                              {widgetType.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDarkMode ? palette.gray[400] : "#666",
                                fontSize: "14px",
                              }}
                            >
                              {widgetType.description}
                            </Typography>
                          </Box>
                        </Box>
                        <ArrowRight
                          size={28}
                          color={isDarkMode ? palette.gray[400] : "#666"}
                        />
                      </Button>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : currentStep === "chatbots" ? (
            <>
              <Fade in={open && !isClosing} timeout={350}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <IconButton
                    onClick={handleBackToMain}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      mr: 2,
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <ArrowLeft size={24} />
                  </IconButton>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      color: isDarkMode ? palette.gray[100] : palette.gray[900],
                    }}
                  >
                    Chatbots
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      ml: "auto",
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <X size={24} />
                  </IconButton>
                </Box>
              </Fade>

              <Fade in={open && !isClosing} timeout={375}>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? palette.gray[300] : "#000",
                    fontSize: "16px",
                    mb: 2,
                  }}
                >
                  Chatbots from flows
                </Typography>
              </Fade>

              <Fade in={open && !isClosing} timeout={400}>
                <Box sx={{ mb: 3 }}>
                  <StyledTextField
                    fullWidth
                    placeholder="Search by name or space"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MagnifyingGlass
                            size={20}
                            color={isDarkMode ? palette.gray[500] : "#666"}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Fade>

              <Fade in={open && !isClosing} timeout={450}>
                <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
                  {isLoadingChatbots ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <Typography
                        color={isDarkMode ? palette.gray[400] : "#666"}
                      >
                        Loading chatbots...
                      </Typography>
                    </Box>
                  ) : filteredChatbots.length === 0 ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <Typography
                        color={isDarkMode ? palette.gray[400] : "#666"}
                      >
                        {searchTerm
                          ? "No chatbots found"
                          : "No chatbots available"}
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {filteredChatbots.map((chatbot, index) => (
                        <Grid item xs={12} key={chatbot.id}>
                          <Fade
                            in={open && !isClosing}
                            timeout={500 + index * 50}
                          >
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => handleAddChatbotWidget(chatbot)}
                              sx={{
                                p: 2,
                                border: `1px solid ${
                                  isDarkMode ? palette.gray[700] : "#e0e0e0"
                                }`,
                                borderRadius: "16px",
                                textTransform: "none",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 3,
                                justifyContent: "space-between",
                                color: isDarkMode ? palette.gray[200] : "#333",
                                "&:hover": {
                                  borderColor: isDarkMode
                                    ? palette.gray[600]
                                    : "#ccc",
                                  backgroundColor: isDarkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(0,0,0,0.02)",
                                  boxShadow: isDarkMode
                                    ? "none"
                                    : "0 4px 12px rgba(0,0,0,0.1)",
                                },
                              }}
                            >
                              <Box sx={{ textAlign: "left" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: isDarkMode
                                      ? palette.gray[100]
                                      : "inherit",
                                  }}
                                >
                                  {chatbot.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDarkMode
                                      ? palette.gray[400]
                                      : "#666",
                                    fontSize: "13px",
                                  }}
                                >
                                  Space: {chatbot.space}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDarkMode
                                      ? palette.gray[200]
                                      : "#000",
                                    fontWeight: 500,
                                  }}
                                >
                                  Add
                                </Typography>
                                <Plus
                                  size={20}
                                  color={
                                    isDarkMode ? palette.gray[200] : "#000"
                                  }
                                />
                              </Box>
                            </Button>
                          </Fade>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Fade>
            </>
          ) : currentStep === "form" ? (
            <>
              <Fade in={open && !isClosing} timeout={350}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <IconButton
                    onClick={handleBackToMain}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      mr: 2,
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <ArrowLeft size={24} />
                  </IconButton>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      color: isDarkMode ? palette.gray[100] : palette.gray[900],
                    }}
                  >
                    Add Form
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      ml: "auto",
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <X size={24} />
                  </IconButton>
                </Box>
              </Fade>

              <Fade in={open && !isClosing} timeout={375}>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? palette.gray[300] : "#000",
                    fontSize: "16px",
                    mb: 2,
                  }}
                >
                  Paste the published form URL
                </Typography>
              </Fade>

              <Fade in={open && !isClosing} timeout={400}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <StyledTextField
                    fullWidth
                    placeholder="Form URL"
                    value={formUrl}
                    onChange={(e) => setFormUrl(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    disabled={!formUrl.trim()}
                    onClick={async () => {
                      await handleAddFormWidget(formUrl);
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Add
                  </Button>
                </Box>
              </Fade>
            </>
          ) : currentStep === "flows" ? (
            <>
              <Fade in={open && !isClosing} timeout={350}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <IconButton
                    onClick={handleBackToMain}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      mr: 2,
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <ArrowLeft size={24} />
                  </IconButton>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      color: isDarkMode ? palette.gray[100] : palette.gray[900],
                    }}
                  >
                    Flows
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: isDarkMode ? palette.gray[400] : "#666",
                      ml: "auto",
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <X size={24} />
                  </IconButton>
                </Box>
              </Fade>

              <Fade in={open && !isClosing} timeout={375}>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? palette.gray[300] : "#000",
                    fontSize: "16px",
                    mb: 2,
                  }}
                >
                  Select a flow to preview
                </Typography>
              </Fade>

              <Fade in={open && !isClosing} timeout={400}>
                <Box sx={{ mb: 3 }}>
                  <StyledTextField
                    fullWidth
                    placeholder="Search by name"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MagnifyingGlass
                            size={20}
                            color={isDarkMode ? palette.gray[500] : "#666"}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Fade>

              <Fade in={open && !isClosing} timeout={450}>
                <Box>
                  {isLoadingFlows ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <Typography
                        color={isDarkMode ? palette.gray[400] : "#666"}
                      >
                        Loading flows...
                      </Typography>
                    </Box>
                  ) : folders.length === 0 && spaces.length === 0 ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <Typography
                        color={isDarkMode ? palette.gray[400] : "#666"}
                      >
                        {searchTerm ? "No flows found" : "No flows available"}
                      </Typography>
                    </Box>
                  ) : (
                    <FlowTreeView
                      folders={folders}
                      spaces={spaces}
                      onFlowSelect={handleAddFlowWidget}
                      searchTerm={searchTerm}
                    />
                  )}
                </Box>
              </Fade>
            </>
          ) : null}
        </Box>
      </Fade>
    </Modal>
  );
}
